import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { AlertCircle, Check, X, Gavel, Clock } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '@/contexts/AuthContext';
import { getPhotoUrl } from '@/utils/photoUrl';
import TipoGanadoCategoriaSelector from '@/components/TipoGanadoCategoriaSelector';
import MultiRazaSelector from '@/components/MultiRazaSelector';

import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix para el icono de Leaflet en React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const MapClickHandler = ({ setLat, setLng }) => {
  useMapEvents({
    click(e) {
      setLat(e.latlng.lat);
      setLng(e.latlng.lng);
    },
  });
  return null;
};

import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const photoGuide = [
  '1. Frente',
  '2. Dorso',
  '3. Costado derecho',
  '4. Tablero',
  '5. Odómetro',
  '6. Costado izquierdo',
  '7. Interior delantero',
  '8. Interior trasero',
  '9. Motor',
  '10. Baúl/Maletero',
];

const SortablePhoto = ({ url, index, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: url });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative aspect-square overflow-hidden rounded-md ring-1 ring-border group"
      data-testid="photo-thumbnail"
    >
      <div {...attributes} {...listeners} className="h-full w-full cursor-grab active:cursor-grabbing">
        <img
          src={getPhotoUrl(url)}
          alt={`Foto ${index + 1}`}
          className="h-full w-full object-cover group-hover:scale-105 transition-transform pointer-events-none"
        />
      </div>
      <div className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded pointer-events-none">
        {index + 1}
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(index); }}
        className="absolute top-1 right-1 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all z-10"
        title="Eliminar foto"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
};

const NuevaPublicacion = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const duplicarId = searchParams.get('duplicar');
  const { user } = useAuth();
  const [step, setStep] = useState('step1');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [sucursales, setSucursales] = useState([]);

  const [formData, setFormData] = useState({
    tipoGanado: '',
    categoria: '',
    raza: '',
    razas: [],
    pesoPromedio: '',
    cantidad: '',
    departamento: '',
    establecimiento: '',
    latitud: -32.5228,
    longitud: -55.7658,
    enlaceVideo: '',
    observaciones: '',
    sanidad: '',
    trazabilidad: '',
    precioBase: '',
    fotos: [],
    estadoPublicacion: 'Borrador',
    autor: user?.nombre || '',
    autorId: user?.id || null,
    esSubasta: false,
    fechaLimiteOfertas: '',
    sucursalId: '',
    tiempoOfertaHoras: '24', // Default 24 horas
    usarTiempoHoras: true, // Toggle para usar horas vs fecha manual
    vendedorNombre: '',
    vendedorTelefono: '',
  });

  const [photoFiles, setPhotoFiles] = useState([]);

  // Cargar sucursales al iniciar
  useEffect(() => {
    loadSucursales();
  }, []);

  // Cargar datos del lote si estamos editando
  useEffect(() => {
    if (editId) {
      loadLoteData(editId);
    }
  }, [editId]);

  // Cargar datos para duplicar publicación
  useEffect(() => {
    if (duplicarId) {
      loadDuplicateData(duplicarId);
    }
  }, [duplicarId]);

  // Asignar sucursal del usuario automáticamente
  useEffect(() => {
    if (user?.sucursalId && !editId && !duplicarId) {
      setFormData(prev => ({ ...prev, sucursalId: user.sucursalId }));
    }
  }, [user, editId, duplicarId]);

  const loadSucursales = async () => {
    try {
      const response = await axios.get(`${API}/sucursales`);
      setSucursales(response.data.filter(s => s.activa));
    } catch (error) {
      console.error('Error loading sucursales:', error);
    }
  };

  const loadLoteData = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/lotes/${id}`);
      const lote = response.data;

      // Cargar datos en el formulario
      setFormData({
        tipoGanado: lote.tipoGanado || '',
        categoria: lote.categoria || '',
        raza: lote.raza || '',
        razas: lote.razas || [],
        pesoPromedio: lote.pesoPromedio || '',
        cantidad: lote.cantidad || '',
        departamento: lote.departamento || '',
        establecimiento: lote.establecimiento || '',
        latitud: lote.latitud || -32.5228,
        longitud: lote.longitud || -55.7658,
        enlaceVideo: lote.enlaceVideo || '',
        observaciones: lote.observaciones || lote.comentarios || '',
        sanidad: lote.sanidad || '',
        trazabilidad: lote.trazabilidad || '',
        precioBase: lote.precioBase || '',
        fotos: lote.fotos || [],
        estadoPublicacion: lote.estadoPublicacion || 'Borrador',
        autor: lote.autor || user?.nombre || '',
        autorId: lote.autorId || user?.id || null,
        esSubasta: lote.esSubasta || false,
        fechaLimiteOfertas: lote.fechaLimiteOfertas
          ? (() => {
              const d = new Date(lote.fechaLimiteOfertas);
              const offset = d.getTimezoneOffset();
              const local = new Date(d.getTime() - offset * 60000);
              return local.toISOString().slice(0, 16);
            })()
          : '',
        sucursalId: lote.sucursalId || '',
        tiempoOfertaHoras: '24',
        usarTiempoHoras: !lote.fechaLimiteOfertas,
        vendedorNombre: lote.vendedorNombre || '',
        vendedorTelefono: lote.vendedorTelefono || '',
      });

      toast.success('Datos cargados para edición');
    } catch (error) {
      console.error('Error loading lote:', error);
      toast.error('Error al cargar los datos del lote');
    } finally {
      setLoading(false);
    }
  };

  const loadDuplicateData = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/lotes/${id}`);
      const lote = response.data;

      setFormData({
        tipoGanado: lote.tipoGanado || '',
        categoria: lote.categoria || '',
        raza: lote.raza || '',
        razas: lote.razas || [],
        pesoPromedio: lote.pesoPromedio || '',
        cantidad: lote.cantidad || '',
        departamento: lote.departamento || '',
        establecimiento: lote.establecimiento || '',
        latitud: lote.latitud || -32.5228,
        longitud: lote.longitud || -55.7658,
        enlaceVideo: lote.enlaceVideo || '',
        observaciones: lote.observaciones || lote.comentarios || '',
        sanidad: lote.sanidad || '',
        trazabilidad: lote.trazabilidad || '',
        precioBase: lote.precioBase || '',
        fotos: lote.fotos || [],
        estadoPublicacion: 'Borrador',
        autor: user?.nombre || lote.autor || '',
        autorId: user?.id || lote.autorId || null,
        esSubasta: lote.esSubasta || false,
        fechaLimiteOfertas: '',
        sucursalId: lote.sucursalId || user?.sucursalId || '',
        tiempoOfertaHoras: '24',
        usarTiempoHoras: true,
        vendedorNombre: lote.vendedorNombre || '',
        vendedorTelefono: lote.vendedorTelefono || '',
      });

      toast.success('Datos cargados para nueva publicación');
    } catch (error) {
      console.error('Error loading lote for duplication:', error);
      toast.error('Error al cargar los datos del lote');
    } finally {
      setLoading(false);
    }
  };

    const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setUploadingImages(true);
    
    try {
      const singleFormData = new FormData();
      singleFormData.append('files', file);
      
      const response = await axios.post(`${API}/upload`, singleFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.urls && response.data.urls.length > 0) {
        setFormData(prev => ({
          ...prev,
          enlaceVideo: `${BACKEND_URL}/uploads/${response.data.urls[0]}`
        }));
        toast.success(`Video subido exitosamente`);
      }
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error('Error al subir el video. Puede que exceda el límite de tamaño.');
    } finally {
      setUploadingImages(false);
    }
  };

  const removePhoto = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      fotos: prev.fotos.filter((_, index) => index !== indexToRemove)
    }));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'video/*': ['.mp4', '.mov', '.webm', '.avi'] },
    maxFiles: 1,
    disabled: loading || uploadingImages
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = formData.fotos.indexOf(active.id);
      const newIndex = formData.fotos.indexOf(over.id);
      setFormData(prev => ({
        ...prev,
        fotos: arrayMove(prev.fotos, oldIndex, newIndex)
      }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.tipoGanado) newErrors.tipoGanado = 'TipoGanado es requerida';
    if (!formData.categoria) newErrors.categoria = 'Categoria es requerido';
    if (!formData.cantidad) newErrors.cantidad = 'Cantidad es requerida';
    if (!formData.pesoPromedio) newErrors.pesoPromedio = 'Peso Promedio es requerido';
    if (!formData.observaciones?.trim()) newErrors.observaciones = 'Las observaciones son obligatorias';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    // No validation required for video, it's optional but recommended
    return true;
  };

  const validateStep4 = () => {
    // El precio base ya no es obligatorio
    return true;
  };

  const handleNext = () => {
    let valid = true;
    if (step === 'step1') valid = validateStep1();
    if (step === 'step2') valid = validateStep2();
    if (step === 'step4') valid = validateStep4();

    if (!valid) return;

    const steps = ['step1', 'step2', 'step3', 'step4', 'step5'];
    const currentIndex = steps.indexOf(step);
    if (currentIndex < steps.length - 1) {
      setStep(steps[currentIndex + 1]);
    }
  };

  const handlePrev = () => {
    const steps = ['step1', 'step2', 'step3', 'step4', 'step5'];
    const currentIndex = steps.indexOf(step);
    if (currentIndex > 0) {
      setStep(steps[currentIndex - 1]);
    }
  };

  const calcFechaLimite = () => {
    // Definimos el offset desde una variable de entorno o usamos GMT-3 por defecto.
    // Nota: Usa el prefijo de tu framework (ej. NEXT_PUBLIC_, VITE_, REACT_APP_)
    const tzOffset = process.env.NEXT_PUBLIC_TZ_OFFSET || '-03:00';

    if (formData.usarTiempoHoras && formData.tiempoOfertaHoras) {
      const horas = parseInt(formData.tiempoOfertaHoras, 10);
      const fecha = new Date();
      fecha.setHours(fecha.getHours() + horas);
      // toISOString() siempre devuelve UTC, lo cual es ideal para guardar en base de datos.
      return fecha.toISOString();
    }

    if (formData.fechaLimiteOfertas) {
      // Aseguramos que el string tenga formato completo (agregando segundos si faltan)
      const fechaBase = formData.fechaLimiteOfertas.length === 16
        ? `${formData.fechaLimiteOfertas}:00`
        : formData.fechaLimiteOfertas;

      // Al agregar '-03:00', forzamos a que se interprete en esa zona horaria
      const fechaConZona = new Date(`${fechaBase}${tzOffset}`);

      return fechaConZona.toISOString();
    }

    return null;
  };

  const handleSaveDraft = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        cantidad: formData.cantidad ? parseInt(formData.cantidad, 10) : 0,
        pesoPromedio: formData.pesoPromedio ? parseFloat(formData.pesoPromedio) : null,
        precioBase: formData.precioBase ? parseFloat(formData.precioBase) : null,
        estadoPublicacion: 'Borrador',
        fechaLimiteOfertas: calcFechaLimite(),
        tipoGanado: formData.tipoGanado || '',
        categoria: formData.categoria || '',
        latitud: formData.latitud ? String(formData.latitud) : null,
        longitud: formData.longitud ? String(formData.longitud) : null,
      };

      // Remove empty sucursalId
      if (!payload.sucursalId) delete payload.sucursalId;

      if (editId) {
        // Actualizar borrador existente
        await axios.put(`${API}/lotes/${editId}`, payload);
        toast.success('Borrador actualizado');
      } else {
        // Crear nuevo borrador
        await axios.post(`${API}/lotes`, payload);
        toast.success('Borrador guardado');
      }

      navigate('/publicaciones');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(editId ? 'Error al actualizar borrador' : 'Error al guardar borrador');
    } finally {
      setSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    
    if (!validateStep1() || !validateStep2() || !validateStep4()) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    isSubmittingRef.current = true;
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        cantidad: parseInt(formData.cantidad, 10),
        pesoPromedio: formData.pesoPromedio ? parseFloat(formData.pesoPromedio) : null,
        precioBase: formData.precioBase ? parseFloat(formData.precioBase) : null,
        estadoPublicacion: 'Enviada',
        fechaLimiteOfertas: calcFechaLimite(),
        latitud: formData.latitud ? String(formData.latitud) : null,
        longitud: formData.longitud ? String(formData.longitud) : null,
      };

      // Remove empty sucursalId
      if (!payload.sucursalId) delete payload.sucursalId;

      if (editId) {
        // Actualizar lote existente
        await axios.put(`${API}/lotes/${editId}`, payload);
        toast.success('Publicación actualizada y enviada');
      } else {
        // Crear nuevo lote
        await axios.post(`${API}/lotes`, payload);
        toast.success('Publicación creada y enviada');
      }

      navigate('/publicaciones');
    } catch (error) {
      console.error('Error saving publication:', error);
      toast.error(editId ? 'Error al actualizar publicación' : 'Error al crear publicación');
    } finally {
      setSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  const progressValue = {
    step1: 20,
    step2: 40,
    step3: 60,
    step4: 80,
    step5: 100,
  }[step];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1989 }, (_, i) => currentYear - i);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Cargando datos...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-semibold tracking-tight">
            {editId ? 'Editar Publicación' : duplicarId ? 'Duplicar Publicación' : 'Nueva Publicación'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {editId ? 'Modifica los datos de tu publicación' : duplicarId ? 'Revisá los datos y guardá como nueva publicación' : 'Completa los 5 pasos para crear una publicación'}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <Progress value={progressValue} className="h-2 mb-6" />

        <Tabs value={step} onValueChange={setStep} className="space-y-6">
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="step1">Básicos</TabsTrigger>
            <TabsTrigger value="step2">Fotos</TabsTrigger>
            <TabsTrigger value="step3">Detalles</TabsTrigger>
            <TabsTrigger value="step4">Precio</TabsTrigger>
            <TabsTrigger value="step5">Revisión</TabsTrigger>
          </TabsList>

          {/* Step 1: Datos Básicos */}
          <TabsContent value="step1" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Datos Básicos del Lote</h2>
              <div className="grid gap-4">
                <TipoGanadoCategoriaSelector
                  tipoGanado={formData.tipoGanado}
                  categoria={formData.categoria}
                  onTipoGanadoChange={(val) => setFormData(prev => ({ 
                    ...prev, 
                    tipoGanado: val, 
                    categoria: val !== prev.tipoGanado ? '' : prev.categoria,
                    razas: val !== prev.tipoGanado ? [] : prev.razas 
                  }))}
                  onCategoriaChange={(val) => setFormData(prev => ({ ...prev, categoria: val }))}
                  errors={errors}
                />



                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cantidad">Cantidad (Cabezas) *</Label>
                    <Input
                      id="cantidad"
                      type="number"
                      value={formData.cantidad}
                      onChange={(e) => setFormData({ ...formData, cantidad: e.target.value })}
                      data-testid="cantidad-input"
                    />
                    {errors.cantidad && (
                      <p className="text-sm text-destructive">{errors.cantidad}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pesoPromedio">Peso Promedio (kg) *</Label>
                    <Input
                      id="pesoPromedio"
                      type="number"
                      value={formData.pesoPromedio}
                      onChange={(e) => setFormData({ ...formData, pesoPromedio: e.target.value })}
                    />
                    {errors.pesoPromedio && (
                      <p className="text-sm text-destructive">{errors.pesoPromedio}</p>
                    )}
                  </div>
                </div>

                <MultiRazaSelector
                  tipoGanado={formData.tipoGanado}
                  totalCantidad={formData.cantidad}
                  value={formData.razas}
                  onChange={(val) => setFormData({ ...formData, razas: val })}
                  errors={errors}
                />




                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sanidad">Sanidad</Label>
                    <Input
                      id="sanidad"
                      placeholder="Ej. Vacunados aftosa, Ivermectina..."
                      value={formData.sanidad}
                      onChange={(e) => setFormData({ ...formData, sanidad: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="trazabilidad">Trazabilidad</Label>
                    <Input
                      id="trazabilidad"
                      placeholder="Ej. Trazados desde el origen"
                      value={formData.trazabilidad}
                      onChange={(e) => setFormData({ ...formData, trazabilidad: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="observaciones">Observaciones *</Label>
                  <Textarea
                    id="observaciones"
                    placeholder="Detalles del lote, certificador, condiciones..."
                    value={formData.observaciones}
                    onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                    className="min-h-[100px]"
                  />
                  {errors.observaciones && (
                    <p className="text-sm text-destructive">{errors.observaciones}</p>
                  )}
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Step 2: Fotos */}
          <TabsContent value="step2" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-2">Video del Lote</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Puedes pegar un enlace de YouTube o arrastrar un archivo de video (MP4, MOV, etc).
              </p>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="enlaceVideo">Enlace del Video (YouTube o URL directa)</Label>
                  <Input
                    id="enlaceVideo"
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={formData.enlaceVideo}
                    onChange={(e) => setFormData({ ...formData, enlaceVideo: e.target.value })}
                  />
                </div>

                <div className="text-center text-sm font-medium my-4 text-muted-foreground">O sube un archivo directamente</div>

                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-md p-8 text-center transition-colors cursor-pointer ${isDragActive ? 'bg-accent/60 border-primary' : 'bg-accent/30 border-border'
                    } ${uploadingImages ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input {...getInputProps()} />
                  {uploadingImages ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-current border-t-transparent text-primary" />
                      <p className="text-sm text-muted-foreground">Subiendo video...</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">
                        {isDragActive
                          ? 'Suelta el video aquí...'
                          : 'Arrastra y suelta un archivo de video (MP4, MOV, máx 50MB)'}
                      </p>
                      <Button variant="secondary" className="mt-3" type="button">
                        Seleccionar video
                      </Button>
                    </>
                  )}
                </div>
                
                {formData.enlaceVideo && formData.enlaceVideo.includes('/uploads/') && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 text-green-800 rounded-md text-sm flex items-center gap-2">
                    <Check className="h-4 w-4" /> Video subido correctamente
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Step 3: Detalles Extra */}
          <TabsContent value="step3" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Ubicación y Establecimiento</h2>
              <div className="grid gap-4">
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="departamento">Departamento</Label>
                    <Select
                      value={formData.departamento}
                      onValueChange={(value) => setFormData({ ...formData, departamento: value })}
                    >
                      <SelectTrigger id="departamento">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        {['Artigas', 'Canelones', 'Cerro Largo', 'Colonia', 'Durazno', 'Flores', 'Florida', 'Lavalleja', 'Maldonado', 'Montevideo', 'Paysandú', 'Río Negro', 'Rivera', 'Rocha', 'Salto', 'San José', 'Soriano', 'Tacuarembó', 'Treinta y Tres'].map(dep => (
                           <SelectItem key={dep} value={dep}>{dep}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="establecimiento">Establecimiento</Label>
                    <Input
                      id="establecimiento"
                      placeholder="Ej. Estancia La Invernada"
                      value={formData.establecimiento}
                      onChange={(e) => setFormData({ ...formData, establecimiento: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2 mt-4">
                  <Label>Ubicación en el Mapa</Label>
                  <p className="text-sm text-muted-foreground mb-2">Haz clic en el mapa para marcar la ubicación del establecimiento.</p>
                  <div className="h-[300px] w-full rounded-md border overflow-hidden relative z-0">
                    <MapContainer center={[formData.latitud || -32.5228, formData.longitud || -55.7658]} zoom={6} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; OpenStreetMap'
                      />
                      <MapClickHandler setLat={(lat) => setFormData({...formData, latitud: lat})} setLng={(lng) => setFormData({...formData, longitud: lng})} />
                      {(formData.latitud && formData.longitud) && (
                        <Marker position={[formData.latitud, formData.longitud]} />
                      )}
                    </MapContainer>
                  </div>
                </div>

                <div className="border-t pt-4 mt-6">
                  <h3 className="font-medium mb-4 text-sm text-muted-foreground">Datos del Cliente (Uso Interno Administrativo)</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="vendedorNombre">Nombre del Productor/Vendedor</Label>
                      <Input
                        id="vendedorNombre"
                        placeholder="Ej. Juan Pérez"
                        value={formData.vendedorNombre || ''}
                        onChange={(e) => setFormData({ ...formData, vendedorNombre: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vendedorTelefono">Teléfono del Productor</Label>
                      <Input
                        id="vendedorTelefono"
                        placeholder="Ej. 099123456"
                        value={formData.vendedorTelefono || ''}
                        onChange={(e) => setFormData({ ...formData, vendedorTelefono: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

              </div>
            </Card>
          </TabsContent>

          {/* Step 4: Precio */}
          <TabsContent value="step4" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Precio y Condiciones de Oferta</h2>
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="precioBase">Precio Base (USD) (Opcional)</Label>
                  <Input
                    id="precioBase"
                    type="number"
                    placeholder="15000"
                    value={formData.precioBase}
                    onChange={(e) => setFormData({ ...formData, precioBase: e.target.value })}
                    data-testid="price-input"
                  />
                  <p className="text-sm text-muted-foreground">
                    Si se deja vacío, las ofertas entrarán directamente sin mínimo.
                  </p>
                </div>

                {/* Subasta */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-start gap-3">
                    <Gavel className="h-5 w-5 text-primary mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="esSubasta"
                          checked={formData.esSubasta}
                          onCheckedChange={(checked) =>
                            setFormData({ ...formData, esSubasta: checked })
                          }
                          data-testid="es-subasta-checkbox"
                        />
                        <Label htmlFor="esSubasta" className="cursor-pointer font-medium">
                          Es subasta
                        </Label>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Activa el modo subasta donde los clientes compiten con ofertas
                      </p>
                    </div>
                  </div>
                </div>

                {/* Fecha límite */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary mt-0.5" />
                    <div className="flex-1 space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="font-medium text-lg">Tiempo Límite de Ofertas</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setFormData(prev => ({ ...prev, usarTiempoHoras: !prev.usarTiempoHoras }))}
                        >
                          Usar {formData.usarTiempoHoras ? 'Fecha Específica' : 'Horas de Duración'}
                        </Button>
                      </div>

                      {formData.usarTiempoHoras ? (
                        <div className="space-y-2">
                          <Label htmlFor="tiempoOfertaHoras">Duración de la publicación para ofertas</Label>
                          <Select
                            value={formData.tiempoOfertaHoras}
                            onValueChange={(value) => setFormData({ ...formData, tiempoOfertaHoras: value })}
                          >
                            <SelectTrigger id="tiempoOfertaHoras">
                              <SelectValue placeholder="Seleccionar duración" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="12">12 horas</SelectItem>
                              <SelectItem value="24">24 horas</SelectItem>
                              <SelectItem value="36">36 horas</SelectItem>
                              <SelectItem value="48">48 horas</SelectItem>
                              <SelectItem value="72">72 horas</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-sm text-muted-foreground">
                            El tiempo corre a partir de que la publicación se envía/confirma.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                          <Label htmlFor="fechaLimite">Fecha específica de cierre</Label>
                          <Input
                            id="fechaLimite"
                            type="datetime-local"
                            value={formData.fechaLimiteOfertas}
                            onChange={(e) => setFormData({ ...formData, fechaLimiteOfertas: e.target.value })}
                            data-testid="fecha-limite-input"
                          />
                          <p className="text-sm text-muted-foreground">
                            Selecciona manualmente la fecha y hora de finalización.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Step 5: Revisión */}
          <TabsContent value="step5" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Revisión Final</h2>
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">TipoGanado:</span>{' '}
                    <span className="font-medium">{formData.tipoGanado}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Categoria:</span>{' '}
                    <span className="font-medium">{formData.categoria}</span>
                  </div>
                  {formData.razas && formData.razas.length > 0 ? (
                    <div>
                      <span className="text-muted-foreground">Razas:</span>{' '}
                      <span className="font-medium">
                        {formData.razas.map(r => `${r.cantidad} ${r.nombre}`).join(', ')}
                      </span>
                    </div>
                  ) : formData.raza && (
                    <div>
                      <span className="text-muted-foreground">Raza:</span>{' '}
                      <span className="font-medium">{formData.raza}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Cantidad (Cabezas):</span>{' '}
                    <span className="font-medium">{formData.cantidad}</span>
                  </div>
                  {formData.pesoPromedio && (
                    <div>
                      <span className="text-muted-foreground">Peso Promedio:</span>{' '}
                      <span className="font-medium">{formData.pesoPromedio} kg</span>
                    </div>
                  )}
                  {formData.departamento && (
                    <div>
                      <span className="text-muted-foreground">Departamento:</span>{' '}
                      <span className="font-medium">{formData.departamento}</span>
                    </div>
                  )}
                  {formData.establecimiento && (
                    <div>
                      <span className="text-muted-foreground">Establecimiento:</span>{' '}
                      <span className="font-medium">{formData.establecimiento}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Cliente:</span>{' '}
                    <span className="font-medium">{formData.vendedorNombre || 'N/A'} {formData.vendedorTelefono ? `(${formData.vendedorTelefono})` : ''}</span>
                  </div>
                </div>
                <div className="border-t pt-4 mt-4 grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Precio Base:</span>{' '}
                    <span className="font-medium text-primary">
                      {formData.precioBase ? `$${parseFloat(formData.precioBase).toLocaleString('en-US')}` : 'Sin Base'}
                    </span>
                  </div>
                  {formData.enlaceVideo && (
                    <div>
                      <span className="text-muted-foreground">Video:</span>{' '}
                      <span className="font-medium text-green-600">
                        Configurado
                      </span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Subasta:</span>{' '}
                    <span className="font-medium">{formData.esSubasta ? 'Sí' : 'No'}</span>
                  </div>
                  {formData.fechaLimiteOfertas && (
                    <div>
                      <span className="text-muted-foreground">Fecha límite:</span>{' '}
                      <span className="font-medium">
                        {new Date(formData.fechaLimiteOfertas).toLocaleString('es-UY')}
                      </span>
                    </div>
                  )}
                  {formData.sanidad && (
                    <div>
                      <span className="text-muted-foreground">Sanidad:</span>{' '}
                      <span className="font-medium">{formData.sanidad}</span>
                    </div>
                  )}
                  {formData.trazabilidad && (
                    <div>
                      <span className="text-muted-foreground">Trazabilidad:</span>{' '}
                      <span className="font-medium">{formData.trazabilidad}</span>
                    </div>
                  )}
                </div>

                {formData.observaciones && (
                  <div>
                    <span className="text-sm text-muted-foreground">Observaciones:</span>
                    <p className="text-sm mt-1">{formData.observaciones}</p>
                  </div>
                )}
                
                {formData.latitud && formData.longitud && (
                  <div className="mt-4">
                    <span className="text-sm text-muted-foreground mb-2 block">Ubicación del Lote:</span>
                    <div className="h-[150px] w-full max-w-[300px] rounded-md border overflow-hidden relative z-0">
                      <MapContainer center={[formData.latitud, formData.longitud]} zoom={7} scrollWheelZoom={false} zoomControl={false} dragging={false} style={{ height: "100%", width: "100%" }}>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <Marker position={[formData.latitud, formData.longitud]} />
                      </MapContainer>
                    </div>
                  </div>
                )}

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Al confirmar el envío, la publicación será visible para los clientes y podrán
                    realizar ofertas.
                  </AlertDescription>
                </Alert>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center py-6">
          <div>
            {step !== 'step1' && (
              <Button
                variant="outline"
                onClick={handlePrev}
                disabled={submitting}
                data-testid="wizard-prev-button"
              >
                Anterior
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleSaveDraft}
              disabled={submitting}
              data-testid="save-draft-button"
            >
              {submitting ? (
                <><div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />Guardando...</>
              ) : 'Guardar Borrador'}
            </Button>
            {step !== 'step5' ? (
              <Button onClick={handleNext} disabled={submitting} data-testid="wizard-next-button">
                Siguiente
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-listing-button">
                {submitting ? (
                  <><div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />Enviando...</>
                ) : 'Confirmar Envío'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NuevaPublicacion;
